// The "new version available" flow (IMP-064).
//
// A deploy publishes a new service worker. The browser downloads it in the
// background; once it is installed and waiting, vite-plugin-pwa calls
// onNeedRefresh (lib/registerSW.ts), which hands it to onUpdateReady() here.
// From there it is applied in one of three ways:
//
//   1. At launch, before anything renders, when it was already downloaded in
//      an earlier session (applyWaitingUpdateBeforeRender, called by main.tsx).
//      The static splash stays up a moment longer; nothing else is visible.
//   2. Silently, when it becomes ready at the start of a session — a fresh
//      launch, or a return after SESSION_GAP_MS in the background — within
//      FRESH_WINDOW_MS, before anything has been tapped or typed, and with no
//      unsaved changes anywhere.
//   3. Otherwise through the card (components/UpdateCard.tsx): Reload applies
//      it now; Later hides it until the start of the next session, when it
//      applies silently (2) — or at the next launch (1).
//
// Never over unsaved work: while any form reports unsaved changes
// (lib/unsavedChanges.ts), nothing here reloads by itself, and the card's
// Reload asks first.
//
// Modelled on Home Assistant's frontend (src/util/register-service-worker.ts
// in home-assistant/frontend) without its 60-second auto-reload countdown: for
// updates that arrive mid-session the owner chose the card (2026-09-26).

import { hasUnsavedChanges } from './unsavedChanges';

export type UpdateStatus = 'none' | 'available' | 'reloading';

export interface UpdateState {
  status: UpdateStatus;
  /** The user chose Later; the card stays hidden until the next session. */
  dismissed: boolean;
}

/** Back after this long in the background = a new session. */
export const SESSION_GAP_MS = 30 * 60_000;
/** How far into a session an update may still apply silently. */
export const FRESH_WINDOW_MS = 15_000;
/** Look for a new version on returning to the app at most this often… */
export const CHECK_THROTTLE_MS = 15 * 60_000;
/** …and this often while it stays open. */
export const CHECK_INTERVAL_MS = 60 * 60_000;
/** If a Reload has not reloaded the page after this long, reload anyway. */
export const RELOAD_FALLBACK_MS = 4_000;

// Loop guard for the reloads this module starts without a user tap (the
// launch-time apply, the Reload fallback and stale-chunk recovery): at most one
// a minute between them. Kept in sessionStorage because it must survive the
// reload it guards. Same idea as Home Assistant's `reloadFresh()` cooldown.
const RELOAD_MARK_KEY = 'onlogic:updateReloadAt';
const RELOAD_COOLDOWN_MS = 60_000;

type Apply = () => void | Promise<void>;

let state: UpdateState = { status: 'none', dismissed: false };
const listeners = new Set<(s: UpdateState) => void>();
let apply: Apply | null = null;
let registration: ServiceWorkerRegistration | null = null;
let sessionStartedAt = Date.now();
let interacted = false;
let hiddenAt: number | null = null;
// Every launch is a check already: the browser looks for a new service worker
// on each navigation.
let lastCheckAt = Date.now();
let tracking = false;

function setState(patch: Partial<UpdateState>): void {
  state = { ...state, ...patch };
  for (const listener of listeners) listener(state);
}

/** Current state, for a component's first render. */
export function getAppUpdateState(): UpdateState {
  return state;
}

/** Subscribe to state changes; returns the unsubscribe function. */
export function subscribeAppUpdate(listener: (s: UpdateState) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * The silent path's rule: only at the very start of a session, before the user
 * has done anything, and never over unsaved work. Pure, so it can be tested on
 * its own.
 */
export function shouldApplySilently(ctx: {
  msSinceSessionStart: number;
  interacted: boolean;
  unsaved: boolean;
}): boolean {
  return !ctx.unsaved && !ctx.interacted && ctx.msSinceSessionStart <= FRESH_WINDOW_MS;
}

/**
 * A new version is installed and waiting (vite-plugin-pwa's onNeedRefresh).
 * `applyFn` makes it take over; the page reloads once it does.
 */
export function onUpdateReady(applyFn: Apply): void {
  apply = applyFn;
  if (state.status === 'reloading') return;
  const silent = shouldApplySilently({
    msSinceSessionStart: Date.now() - sessionStartedAt,
    interacted,
    unsaved: hasUnsavedChanges(),
  });
  if (silent) {
    applyUpdate();
    return;
  }
  setState({ status: 'available', dismissed: false });
}

/** Apply the waiting version now (the card's Reload, or the silent path). */
export function applyUpdate(): void {
  if (!apply || state.status === 'reloading') return;
  setState({ status: 'reloading', dismissed: false });
  const run = apply;
  void Promise.resolve()
    .then(run)
    .catch(() => {
      // Could not reach the waiting worker; the card offers Reload again.
      setState({ status: 'available', dismissed: false });
    });
  // Normally the new worker takes over and the page reloads within a second.
  // If that stalls, reload anyway — but only with the once-a-minute slot: a
  // worker that never takes over would otherwise turn the silent path into a
  // reload at every launch. Without the slot, the card comes back instead.
  window.setTimeout(() => {
    if (state.status !== 'reloading') return;
    if (takeReloadSlot()) window.location.reload();
    else setState({ status: 'available', dismissed: false });
  }, RELOAD_FALLBACK_MS);
}

/** Later: hide the card; the update applies silently at the next session. */
export function dismissUpdate(): void {
  setState({ dismissed: true });
}

/**
 * The new version now controls this page — after this tab's Reload, or because
 * another tab applied it (vite-plugin-pwa's onNeedReload). This page runs the
 * old build under the new worker, whose cleanup removed the old build's cached
 * files, so it should reload soon — but not over unsaved work: then the card
 * offers the reload instead.
 */
export function onNewVersionInControl(): void {
  if (state.status === 'reloading' || !hasUnsavedChanges()) {
    window.location.reload();
    return;
  }
  apply = () => window.location.reload();
  setState({ status: 'available', dismissed: false });
}

/** Look for a new version now (a no-op until the worker is registered). */
export function checkForUpdate(): void {
  if (!registration) return;
  lastCheckAt = Date.now();
  // Offline or unreachable: nothing to do, the next check tries again.
  registration.update().catch(() => undefined);
}

function startSession(): void {
  sessionStartedAt = Date.now();
  interacted = false;
}

function onInteract(): void {
  interacted = true;
}

function onVisibilityChange(): void {
  if (document.visibilityState === 'hidden') {
    hiddenAt = Date.now();
    return;
  }
  const awayMs = hiddenAt === null ? 0 : Date.now() - hiddenAt;
  hiddenAt = null;
  if (awayMs >= SESSION_GAP_MS) {
    // Back after a while: on phones the app often stays in memory for days,
    // so this is a new session in all but name.
    startSession();
    if (state.status === 'available') {
      if (!hasUnsavedChanges()) {
        applyUpdate();
        return;
      }
      // Something is still unsaved from before: bring the card back instead.
      setState({ dismissed: false });
    }
  }
  if (Date.now() - lastCheckAt >= CHECK_THROTTLE_MS) checkForUpdate();
}

/**
 * Start tracking the session: interactions (so the silent path only runs
 * before the user has done anything) and visibility (new sessions, update
 * checks). Called once by main.tsx before the first render, so the very first
 * tap counts.
 */
export function startAppUpdateTracking(): void {
  if (tracking) return;
  tracking = true;
  startSession();
  const opts = { capture: true, passive: true } as const;
  window.addEventListener('pointerdown', onInteract, opts);
  window.addEventListener('keydown', onInteract, opts);
  window.addEventListener('wheel', onInteract, opts);
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.setInterval(() => {
    if (document.visibilityState === 'visible') checkForUpdate();
  }, CHECK_INTERVAL_MS);
}

/** The registered service worker, once vite-plugin-pwa has it. */
export function setUpdateRegistration(reg: ServiceWorkerRegistration | undefined): void {
  registration = reg ?? null;
}

/** Take the one-a-minute slot for an unprompted reload; false if it is used. */
export function takeReloadSlot(): boolean {
  try {
    const now = Date.now();
    const last = Number(sessionStorage.getItem(RELOAD_MARK_KEY)) || 0;
    if (now - last < RELOAD_COOLDOWN_MS) return false;
    sessionStorage.setItem(RELOAD_MARK_KEY, String(now));
    return true;
  } catch {
    // No storage (some private modes): without a durable guard a reload could
    // repeat forever, so do not reload unprompted at all.
    return false;
  }
}

/**
 * Launch-time apply (path 1): when the previous session left a new version
 * waiting, let it take over and reload before rendering anything. Resolves
 * true when the page is reloading (the caller must not render), false to
 * render normally. Bounded: a slow registry lookup or a worker that does not
 * take over within a few seconds just renders the current version.
 */
export async function applyWaitingUpdateBeforeRender(): Promise<boolean> {
  if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) return false;
  let reg: ServiceWorkerRegistration | undefined;
  try {
    reg = await Promise.race([
      navigator.serviceWorker.getRegistration(),
      new Promise<undefined>((resolve) => window.setTimeout(() => resolve(undefined), 500)),
    ]);
  } catch {
    return false;
  }
  const waiting = reg?.waiting;
  if (!waiting || !takeReloadSlot()) return false;
  const tookOver = new Promise<boolean>((resolve) => {
    const timer = window.setTimeout(() => resolve(false), 3_000);
    navigator.serviceWorker.addEventListener(
      'controllerchange',
      () => {
        window.clearTimeout(timer);
        resolve(true);
      },
      { once: true },
    );
  });
  waiting.postMessage({ type: 'SKIP_WAITING' });
  if (!(await tookOver)) return false;
  window.location.reload();
  return true;
}

/**
 * A lazy chunk of this build failed to load (Vite's `vite:preloadError`). After
 * a deploy that usually means this page still runs the previous build and its
 * files are gone, so reload onto the current one — at most once a minute, so a
 * genuinely broken chunk cannot cause a reload loop.
 */
export function recoverFromStaleChunk(): void {
  if (takeReloadSlot()) window.location.reload();
}

/** Test-only: reset module state between tests. */
export function resetAppUpdateForTests(): void {
  state = { status: 'none', dismissed: false };
  listeners.clear();
  apply = null;
  registration = null;
  sessionStartedAt = Date.now();
  interacted = false;
  hiddenAt = null;
  lastCheckAt = Date.now();
}
